import { _ } from "../../../polyglot/polyglot.js";
import { getCurrencies } from "../../../helpers/currency-formatting-util.js";
import { isNotNull } from "../../../helpers/object-util.js";
import { select } from "../../../elements/index.js";
import { trashSolidAltIcon } from "../../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import Agency from "../../../../models/Agency.js";
import regionWidget from "../../regionWidget.js";

const inactiveYearValue = "inactive-year-value";

/**
 * Displays a form for managing the editable aspects of a fiscal year:
 * budgets, chart of accounts and currency.
 * You can also delete the fiscal year.
 *
 * @param {FiscalYear} [fiscalYear] - Fiscal year to edit
 * @param {ChartOfAccounts[]} chartsOfAccounts - List of selectable chart of accounts
 * @param {function():boolean} [showLastActiveMonthPickerPredicate] - Should
 *   return true for the active period select to become visible
 */
export default class FiscalYearEditorWidget extends Widget {
	constructor({
		fiscalYear,
		chartsOfAccounts = [],
		showLastActiveMonthPickerPredicate = () => false,
	}) {
		super(...arguments);

		this._fiscalYear = fiscalYear;

		this._chartsOfAccounts = [...chartsOfAccounts].sort((a, b) =>
			a.compare(b),
		);

		this.onSave = this._events.createEvent();
		this.onDelete = this._events.createEvent();
		this.onDeleteBudget = this._events.createEvent();

		this._chartOfAccountsSelection = this._makeChartOfAccountsSelection();
		this._currencySelection = this._makeCurrencySelection();
		this._budgetSelection = this._makeBudgetSelection();
		this._deleteBudgetButton = this._makeDeleteBudgetButton();

		this._lastActiveTransactionDateSelection =
			this._makeLastActiveTransactionDateSelection();

		this._showLastActiveMonthPickerPredicate =
			showLastActiveMonthPickerPredicate;
	}

	setChartsOfAccounts(chartsOfAccounts) {
		this._chartsOfAccounts = chartsOfAccounts.sort((a, b) => a.compare(b));
		this._chartOfAccountsSelection.update();
	}

	renderContentOn(html) {
		if (!this._fiscalYear) {
			return;
		}

		html.form(
			{ klass: "fiscal-year-editor clearfix" },
			this._selectionRenderer({
				label: _("Budget"),
				dropdown: this._budgetSelection,
			}),
			this._selectionRenderer({
				label: _("Chart of Accounts"),
				dropdown: this._chartOfAccountsSelection,
			}),
			this._selectionRenderer({
				label: _("Currency"),
				dropdown: this._currencySelection,
			}),
			(html) => {
				if (!this._showLastActiveMonthPickerPredicate()) return;

				html.render(
					this._selectionRenderer({
						label: _("Last active month"),
						dropdown: this._lastActiveTransactionDateSelection,
					}),
				);
			},

			this._renderActionsOn.bind(this),
		);

		html.render(this._deleteBudgetButton);
	}

	_makeChartOfAccountsSelection() {
		this._selectedChartOfAccounts = this._fiscalYear.getChartOfAccountsId();

		return this._makeSelection({
			name: "chart-of-accounts",
			getItems: () =>
				this._chartsOfAccounts.map((coa) => ({
					label: this._makeChartOfAccountsName(coa),
					value: coa.getId(),
				})),
			getSelected: () => this._fiscalYear.getChartOfAccountsId(),
			onChange: (value) => (this._selectedChartOfAccounts = value),
		});
	}

	_makeCurrencySelection() {
		this._selectedCurrency = this._fiscalYear.getCurrencyCultureName();

		return this._makeSelection({
			name: "currency",
			getItems: () =>
				getCurrencies().map((currentCurrency) => ({
					label: currentCurrency.iso4217,
					value: currentCurrency.locales[0],
				})),
			getSelected: () => this._fiscalYear.getCurrencyCultureName(),
			onChange: (value) => (this._selectedCurrency = value),
		});
	}

	_makeBudgetSelection() {
		this._selectedBudget = this._fiscalYear.getCurrentBudget();

		return this._makeSelection({
			name: "budget",
			getItems: () =>
				this._fiscalYear.getBudgets().map((budget) => ({
					label: budget.getName(),
					value: budget,
				})),
			getSelected: () => this._fiscalYear.getCurrentBudget(),
			onChange: (value) => (this._selectedBudget = value),
			allowEmptySelection: true,
		});
	}

	_makeDeleteBudgetButton() {
		return regionWidget({
			content: (html) => {
				if (!this._selectedBudget) {
					return;
				}

				html.a(
					{
						klass: "btn btn-danger remove-budget",
						"data-test": "delete-budget",
						click: this._deleteBudget.bind(this),
						title: _("Delete budget"),
					},
					trashSolidAltIcon,
				);
			},
		});
	}

	_makeLastActiveTransactionDateSelection() {
		const getSelected = () =>
			this._fiscalYear.isActive()
				? this._fiscalYear.lastActiveMonth().getEnd()
				: inactiveYearValue;

		this._selectedLastActiveTransactionDate = getSelected();

		return this._makeSelection({
			name: "last-active-month",
			getItems: () =>
				this._createLastActiveTransactionDateSelectionElements(),
			getSelected,
			onChange: (value) =>
				(this._selectedLastActiveTransactionDate = value),
		});
	}

	_makeSelection({
		name,
		getItems,
		getSelected,
		onChange,
		allowEmptySelection,
	}) {
		return regionWidget({
			content: (html) =>
				html.render(
					select({
						name,
						items: getItems(),
						selected: getSelected(),
						onChange,
						allowEmptySelection,
					}),
				),
		});
	}

	_selectionRenderer({ label, dropdown }) {
		return (html) =>
			html.div(
				{ class: "row" },
				html.label(
					{ class: "control-label col-sm-4 col-md-4  col-lg-4" },
					label,
				),
				html.div({ class: "col-sm-8 col-md-8  col-lg-8" }, dropdown),
			);
	}

	_renderActionsOn(html) {
		html.div(
			{ class: "row pull-right" },
			html.div(
				{ class: "col-sm-12 col-md-12  col-lg-12" },
				html.ul(
					{ class: "list-inline" },
					html.li(
						html.a(
							{
								class: "btn btn-primary",
								click: this._saveFiscalYear.bind(this),
							},
							_("Update settings"),
						),
					),
					html.li(
						html.a(
							{
								class: "btn btn-danger",
								click: this._deleteFiscalYear.bind(this),
							},
							_("Delete fiscal year"),
						),
					),
				),
			),
		);
	}

	_createLastActiveTransactionDateSelectionElements() {
		const inactiveYearSelection = {
			label: _("Inactive year"),
			value: inactiveYearValue,
		};

		const monthsSelection = this._fiscalYear.asMonths().map((month) => ({
			label: month.toSmartString(),
			value: month.getEnd(),
		}));

		return [inactiveYearSelection].concat(monthsSelection);
	}

	_saveFiscalYear() {
		this._fiscalYear.setChartOfAccountsId(this._selectedChartOfAccounts);
		this._fiscalYear.setCurrencyCultureName(this._selectedCurrency);

		this._fiscalYear.setLastActiveTransactionDate(
			this._getLastActiveTransactionDateSelection(),
		);

		// The budget can be null
		if (isNotNull(this._selectedBudget)) {
			this._fiscalYear.setCurrentBudget(this._selectedBudget);
		}

		this.onSave.trigger(this._fiscalYear);
	}

	_getLastActiveTransactionDateSelection() {
		return this._selectedLastActiveTransactionDate === inactiveYearValue
			? null
			: this._selectedLastActiveTransactionDate;
	}

	_deleteFiscalYear() {
		this.onDelete.trigger(this._fiscalYear);
	}

	_deleteBudget() {
		this.onDeleteBudget.trigger(
			this._selectedBudget,
			this._fiscalYear,
			() => {
				this._deleteBudgetButton.update();
				this._budgetSelection.update();
			},
		);
	}

	_makeChartOfAccountsName(chartOfAccounts) {
		const name = chartOfAccounts.getName();

		if (this._isAgencyChartOfAccounts(chartOfAccounts))
			return _("{entityName} (shared)", { entityName: name });

		return name;
	}

	_isAgencyChartOfAccounts(chartOfAccounts) {
		return chartOfAccounts.getOwnerType() === Agency.getType();
	}
}
